import { measurementPointToPixelFromScales, resolveMeasurementPointFromValues, summarizeMeasurement as summarizeMeasurementCore, } from "@vninvestcharts/engine-core";
// ─── Ported from src/lib/utils (Phase 1, task 3.1) ────────────────────────────
// `measuring.ts` used to import getCurrentItem/getXValue from
// ../utils/ChartDataUtil, which is an escape out of src/lib/drawing. The two
// functions plus the three tiny helpers they need are pure, so they now live
// here and can be copied verbatim into engine-drawing (Phase 2). Behaviour is
// intentionally identical to the originals, including the binary-search tie
// breaking and the `>=`/`<=` comparisons on the accessor value.
function isDefined(d) {
    return d !== null && typeof d != "undefined";
}
function lastItem(array, accessor) {
    if (accessor && array) {
        for (let i = array.length - 1; i >= 0; i--) {
            const value = array[i];
            if (isDefined(accessor(value)))
                return value;
        }
        return undefined;
    }
    const length = array ? array.length : 0;
    return length ? array[length - 1] : undefined;
}
function getClosestItemIndexes(array, value, accessor) {
    let lo = 0;
    let hi = array.length - 1;
    while (hi - lo > 1) {
        const mid = Math.round((lo + hi) / 2);
        if (accessor(array[mid]) <= value) {
            lo = mid;
        }
        else {
            hi = mid;
        }
    }
    if (accessor(array[lo]).valueOf() === value.valueOf())
        hi = lo;
    if (accessor(array[hi]).valueOf() === value.valueOf())
        lo = hi;
    if (accessor(array[lo]) < value && accessor(array[hi]) < value)
        lo = hi;
    if (accessor(array[lo]) > value && accessor(array[hi]) > value)
        hi = lo;
    return { left: lo, right: hi };
}
function getClosestItem(array, value, accessor) {
    const { left, right } = getClosestItemIndexes(array, value, accessor);
    if (left === right) {
        return array[left];
    }
    return (Math.abs(accessor(array[left]) - value) < Math.abs(accessor(array[right]) - value))
        ? array[left]
        : array[right];
}
function getCurrentItem(xScale, xAccessor, mouseXY, plotData) {
    if (!plotData || plotData.length === 0)
        return undefined;
    let xValue;
    let item;
    if (xScale.invert) {
        xValue = xScale.invert(mouseXY[0]);
        item = getClosestItem(plotData, xValue, xAccessor);
    }
    else {
        const d = xScale.range().map((rangeValue, idx) => ({ x: Math.abs(rangeValue - mouseXY[0]), idx })).reduce((a, b) => a.x < b.x ? a : b);
        item = isDefined(d) ? plotData[d.idx] : plotData[0];
    }
    return item;
}
function getXValue(xScale, xAccessor, mouseXY, plotData) {
    if (!plotData || plotData.length === 0) {
        return isDefined(xScale.invert) ? xScale.invert(mouseXY[0]) : undefined;
    }
    let xValue;
    let item;
    if (xScale.invert) {
        xValue = xScale.invert(mouseXY[0]);
        if (xValue > xAccessor(lastItem(plotData)) && xScale.value) {
            return Math.round(xValue);
        }
        item = getClosestItem(plotData, xValue, xAccessor);
    }
    else {
        const d = xScale.range().map((rangeValue, idx) => ({ x: Math.abs(rangeValue - mouseXY[0]), idx })).reduce((a, b) => a.x < b.x ? a : b);
        item = isDefined(d) ? plotData[d.idx] : plotData[0];
    }
    return xAccessor(item);
}
function resolveChartConfig(moreProps) {
    const chartConfigList = moreProps.chartConfig;
    if (Array.isArray(chartConfigList)) {
        return chartConfigList.find((each) => each.id === moreProps.chartId) || chartConfigList[0];
    }
    return chartConfigList;
}
export function resolveMeasurementPoint(moreProps, mousePosition = moreProps.mouseXY) {
    const chartConfig = resolveChartConfig(moreProps);
    const { xScale, xAccessor, plotData } = moreProps;
    if (!chartConfig || typeof chartConfig.yScale?.invert !== "function") {
        return undefined;
    }
    if (!Array.isArray(mousePosition) || mousePosition.length < 2) {
        return undefined;
    }
    if (!Array.isArray(plotData) || plotData.length === 0 || typeof xAccessor !== "function") {
        return undefined;
    }
    const currentItem = getCurrentItem(xScale, xAccessor, mousePosition, plotData);
    const xValue = currentItem ? xAccessor(currentItem) : getXValue(xScale, xAccessor, mousePosition, plotData);
    const dateValue = xValue instanceof Date ? xValue : new Date(xValue);
    const priceValue = chartConfig.yScale.invert(mousePosition[1]);
    return resolveMeasurementPointFromValues({
        plotData,
        xAccessor,
        dateValue,
        priceValue,
    });
}
export function measurementPointToPixel(point, moreProps) {
    const chartConfig = resolveChartConfig(moreProps);
    if (!chartConfig) {
        return undefined;
    }
    const { xScale } = moreProps;
    if (typeof xScale !== "function" || typeof chartConfig.yScale !== "function") {
        return undefined;
    }
    return measurementPointToPixelFromScales({
        point,
        xScale,
        yScale: chartConfig.yScale,
    });
}
export function summarizeMeasurement(selection) {
    return summarizeMeasurementCore(selection);
}
/**
 * Compute a live measurement summary between two drawing points using chart scales.
 *
 * Returns null when either point is missing or scales are not yet available.
 */
export function computeLiveMeasurement(p0, p1, scales) {
    if (!scales || typeof scales.xScale !== "function" || typeof scales.yScale !== "function") {
        return null;
    }
    const px0 = { x: scales.xScale(new Date(p0.x)), y: scales.yScale(p0.y) };
    const px1 = { x: scales.xScale(new Date(p1.x)), y: scales.yScale(p1.y) };
    const priceDelta = p1.y - p0.y;
    const pricePct = p0.y !== 0 ? priceDelta / p0.y : 0;
    const timeDeltaMs = p1.x - p0.x;
    // Bar count: number of bars in the plot data between the two timestamps
    let barCount = 0;
    if (scales.plotData && scales.xAccessor && scales.plotData.length > 1) {
        const dates = scales.plotData.map((d) => {
            const v = scales.xAccessor(d);
            return v instanceof Date ? v.getTime() : d.date instanceof Date ? d.date.getTime() : Number(v);
        });
        const t0 = p0.x;
        const t1 = p1.x;
        const tMin = Math.min(t0, t1);
        const tMax = Math.max(t0, t1);
        barCount = dates.filter((t) => t >= tMin && t <= tMax).length - 1;
        if (barCount < 0)
            barCount = 0;
    }
    else if (scales.plotData && scales.plotData.length > 1) {
        // Fallback: estimate from timeDeltaMs vs average bar duration
        const first = scales.plotData[0];
        const last = scales.plotData[scales.plotData.length - 1];
        const totalMs = (last.date ?? last.Date ?? 0) - (first.date ?? first.Date ?? 0);
        const avgBarMs = totalMs / (scales.plotData.length - 1);
        if (avgBarMs > 0) {
            barCount = Math.round(Math.abs(timeDeltaMs) / avgBarMs);
        }
    }
    return { priceDelta, pricePct, timeDeltaMs, barCount, px0, px1 };
}
